"use client";

import React, { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { Bars3Icon } from "@heroicons/react/24/outline";
import Link from "next/link";
import Logo from "./Logo";
import MobileMenu from "./MobileMenu";
import type { NavigationItem } from "@/types/navigation";

interface HeaderClientProps {
  navigationItems: NavigationItem[];
}

export default function HeaderClient({
  navigationItems,
}: HeaderClientProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const pathname = usePathname();
  const headerRef = useRef<HTMLElement>(null);

  // Determine active navigation items based on current pathname
  const itemsWithActiveState: NavigationItem[] = navigationItems.map(
    (item) => {
      if (item.external) {
        return { ...item, isActive: false };
      }
      
      // Exact match for home page
      if (item.href === "/") {
        return { ...item, isActive: pathname === "/" };
      }
      
      // For other pages, check if pathname starts with the href
      // This handles nested routes like /about/team matching /about
      return {
        ...item,
        isActive: pathname === item.href || pathname.startsWith(`${item.href}/`),
      };
    }
  );

  // Close mobile menu when pathname changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Intersection Observer to detect when hero section is out of view
  useEffect(() => {
    // Look for the first section element (should be hero section on homepage)
    // Also check for sections with hero-related aria-labels
    const heroSection = document.querySelector(
      'section[aria-label*="Introduction"], section[aria-label*="Hero"], main > section:first-of-type'
    ) as HTMLElement | null;

    if (!heroSection) {
      // Fallback: make sticky after scrolling past header height
      const handleScroll = () => {
        if (window.scrollY > 100) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      };

      window.addEventListener("scroll", handleScroll);
      handleScroll(); // Check initial state

      return () => window.removeEventListener("scroll", handleScroll);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When hero section is not visible (scrolled past), make header sticky
          setIsSticky(!entry.isIntersecting);
        });
      },
      {
        threshold: 0,
        rootMargin: "-100px 0px 0px 0px", // Trigger slightly before hero is completely out of view
      }
    );

    observer.observe(heroSection);

    return () => {
      observer.disconnect();
    };
  }, [pathname]); // Re-run when pathname changes to find new hero section

  return (
    <header
      ref={headerRef}
      className={`bg-slate-900 transition-all duration-300 ${
        isSticky
          ? "sticky top-0 z-50 backdrop-blur-md bg-slate-900/95 shadow-lg"
          : "relative"
      }`}
    >
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between gap-8 px-6 py-6 lg:px-8 lg:py-11"
        aria-label="Global"
      >
        {/* Logo - Left */}
        <div className="flex flex-shrink-0 lg:flex-1">
          <Logo />
        </div>

        {/* Desktop Navigation - Center (hidden on mobile) */}
        {navigationItems.length > 0 && (
          <div className="hidden lg:flex lg:flex-1 lg:justify-center">
            <ul className="flex items-center justify-end gap-6">
              {itemsWithActiveState.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    className={`text-base font-medium leading-6 text-center whitespace-nowrap px-4 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-solid focus:ring-offset-2 focus:ring-offset-slate-900 ${
                      item.isActive
                        ? "text-white border-b-2 border-brand-solid pt-3 pb-2.5"
                        : "text-slate-200 hover:text-white py-3"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Mobile menu button */}
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-3 text-slate-200 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-solid focus:ring-offset-2 focus:ring-offset-slate-900"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open main menu"
            aria-expanded={mobileMenuOpen}
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navigationItems={itemsWithActiveState}
      />
    </header>
  );
}

